import type { ComponentType } from 'react';
import type { ModuleMeta } from './meta';
import { fillText } from './product';

export { APP_NAME, appName, appToken, DEFAULT_APP_NAME, fillText, setAppName } from './product';
export { LEGACY_STORAGE_PREFIX, STORAGE_PREFIX, migrateBrowserStorage, migrateLegacyStorage, renamedStorageKey } from './storageMigration';
export type { StorageLike, StorageMigrationResult } from './storageMigration';

type Lazy = () => Promise<{ default: ComponentType<Record<string, unknown>> }>;
/** A lazy slot component: typed with the slot's own props (the owner's `shared/slots.ts`). */
type SlotLazy = () => Promise<{ default: ComponentType<never> }>;

export interface NavItem {
  id: string;
  label: string;
  /** lucide icon name, e.g. 'circle-dollar-sign' */
  icon: string;
  to: string;
  order: number;
  /** Optional count badge (e.g. overdue activities): a component the shell renders at the item's right edge. */
  badge?: Lazy;
  /**
   * Path prefix that makes this item current (default: `to`). Contacts uses '/contacts' so People,
   * Organizations and the detail pages all highlight it. A list names several prefixes (Pulse also owns `/sequences`,
   * whose page keeps the Pulse panel, ADR-657).
   */
  match?: string | readonly string[];
  /**
   * Secondary panel shown next to the side menu while this item is current (ui-patterns.md §1),
   * e.g. Contacts → People · Organizations. Toggled with `[` / `]`.
   */
  secondary?: Lazy;
  /**
   * The shell's home page ("/") opens the first nav item marked `landing` (README principle 2: the
   * pipeline is home). Without one, the home page shows its placeholder.
   */
  landing?: boolean;
  /**
   * Where the item sits by default: in the side menu (`main`, the default) or in the **More** fly-out
   * (`more`), as Pipedrive's default menu puts Projects, Products, Marketplace… (app-frame.md › Side menu).
   * Each user can rearrange the menu in Interface preferences (ADR-140).
   */
  placement?: 'main' | 'more';
  /**
   * The ⓘ after the top-bar title on this item's pages ("Deals ⓘ", ADR-144): a short description shown in a
   * tooltip. A route's own `info` takes precedence.
   */
  info?: string;
  /** A small BETA badge after the label, in the side menu and in More (Pipedrive's Pulse, ADR-820). */
  beta?: boolean;
}

/** Where an app stands for the signed-in user (Marketplace card, ADR-821). */
export type AppStatus = 'installed' | 'not-connected' | 'needs-setup';

/** The Marketplace categories (ADR-821); the page lists those that have cards. */
export type AppCategory = 'messaging' | 'email-calendar' | 'documents' | 'calling' | 'developer';

/**
 * An app card on the **Marketplace** page (`/app-catalog`, ADR-821): one of BOLTcrm's own integrations, contributed by the
 * module that owns it. The card links to the existing settings page that sets it up; nothing is loaded from a third party.
 */
export interface AppCard {
  id: string;
  name: string;
  /** One line about what the app does. */
  description: string;
  category: AppCategory;
  /** Nav icon name (as `NavItem.icon`). */
  icon: string;
  /** The settings page that installs, connects or configures the app. */
  to: string;
  /** Hook, called once per rendered card: where the app stands for this user (`undefined` while loading). */
  useStatus: () => AppStatus | undefined;
  /** Permission key needed to manage the app; without it the card shows no link. */
  permission?: string;
  order: number;
}

/**
 * A contribution into another module's slot (module-system.md §5). The component is lazy and gets the
 * slot's props. `label` and `align` are for column slots (`*.list.columns`): the column header and its
 * alignment (counts and money are right-aligned).
 */
export interface SlotContribution {
  id: string;
  component: SlotLazy;
  order: number;
  label?: string;
  align?: 'start' | 'end';
  /**
   * Column slots: `false` keeps the column out of the list's default columns (it is still in the column picker), so a
   * module's column doesn't change the verified default set (custom scoring's Score column, ADR-552).
   */
  defaultVisible?: boolean;
  /** Column slots: the header sorts the list by this column's id (`<module>.<id>`), which the list's API must accept as a sort key. */
  sortable?: boolean;
  /**
   * A hook the host calls on every render, for every contribution that has one, to leave the contribution out (e.g.
   * the scheduler's composer tab while the user's mailbox is synced, ADR-424). Hosts that honour it say so.
   */
  useHidden?: () => boolean;
}

/** Props the shell passes to a quick-add dialog. */
export interface QuickAddDialogProps {
  onClose: () => void;
}

export interface QuickAddItem {
  id: string;
  label: string;
  icon: string;
  /** Pipedrive quick-add letter (search-and-quick-add.md). */
  key?: string;
  order: number;
  /** The add dialog. The shell renders it with an `onClose` prop when the item is chosen. */
  open: () => Promise<{ default: ComponentType<QuickAddDialogProps> }>;
}

/** Props the shell passes to a search category's in-place opener. */
export interface SearchOpenProps {
  id: string;
  onClose: () => void;
}

/**
 * A global-search category (search-and-quick-add.md): the web half of a module's API search provider. The
 * shell lists the categories of every enabled module in `order` after "All categories", so a new module's
 * records join search without platform changes.
 */
export interface SearchCategory {
  /** The entity type the module's API search provider serves. */
  type: string;
  /** The category's name in the search panel, e.g. "People". */
  label: string;
  /** Nav icon name for the category row (as `NavItem.icon`). */
  icon: string;
  /**
   * Position in the panel. Pipedrive's order: People 10, Organizations 20, Activities 30, Deals 40, Leads 50,
   * Projects 60, Products 70, Files 80.
   */
  order: number;
  /** Opens a result over the current page (activities' edit dialog) instead of navigating to its `url`. */
  open?: () => Promise<{ default: ComponentType<SearchOpenProps> }>;
}

/** Web half of a module (module-system.md §4). */
export interface WebModule {
  meta: ModuleMeta;
  /**
   * Pages. `title` is the top-bar title; with `section` the top bar shows a breadcrumb
   * "section / title" instead (e.g. "Contacts / People", contacts.md §5).
   */
  routes?: {
    path: string;
    title: string;
    section?: string;
    component: Lazy;
    /** The page fills the main area edge to edge (boards, lists, detail pages) instead of the padded frame. */
    fullBleed?: boolean;
    /** The top-bar ⓘ text for this page, when it differs from its nav item's `info` (ADR-144). */
    info?: string;
  }[];
  nav?: NavItem[];
  /** App cards for the Marketplace page (ADR-821). */
  apps?: AppCard[];
  quickAdd?: QuickAddItem[];
  /** Global-search categories for the entity types this module's API search providers serve. */
  search?: SearchCategory[];
  slots?: Record<string, SlotContribution[]>;
  /**
   * Settings pages. `path` is the route under /settings (e.g. `company/lost-reasons` →
   * `/settings/company/lost-reasons`, default `key`); `icon` is a nav icon name; `order` places the page within
   * its group (lower first; pages without one follow in module order), e.g. Pipedrive's TOOLS order.
   * `company-settings` pages are **tabs of Company settings** (ADR-142: General · Activities · Currencies · Lost
   * reasons · Leads Inbox), with paths under `company/` and Pipedrive's tab positions in tens; the frame shows the
   * "Company settings" title and the tabs, so such a page starts with its own content, not a page title.
   * `redirectFrom` lists old paths under /settings that now redirect here (a page that moved).
   * `tools` and `apps` pages make up **Tools and apps**, an area with its own panel (ADR-270): TOOLS in Pipedrive's
   * order, where `order` is Pipedrive's position in tens (Automations 10, Automatic assignment 20, AI 30, Phone calls
   * 40, Products 50, Webhooks 60, Documents 70, Import data 80, Export data 90, Merge duplicates 100, Restore data
   * 110; a position no module fills shows disabled), then BOLTcrm's own tools pages (any other `order`) after a
   * divider; and APPS (Slack 10, Asana 20, WhatsApp 30, …), which lists only the apps that exist.
   */
  settings?: {
    key: string;
    group: 'company' | 'company-settings' | 'personal' | 'tools' | 'apps' | 'security';
    label: string;
    component: Lazy;
    path?: string;
    icon?: string;
    order?: number;
    redirectFrom?: string[];
  }[];
}

export const defineWebModule = <M extends WebModule>(mod: M): M => mod;

/**
 * A module's string table (CLAUDE.md: every string goes through i18n). English only until P5;
 * `{name}` placeholders are filled from `vars`.
 */
export function createTranslator<M extends Record<string, string>>(en: M) {
  return (key: keyof M & string, vars?: Record<string, string | number>): string => {
    return fillText(en[key] ?? key, vars);
  };
}

/** Error from the BOLTcrm API (api.md › Responses). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

/** Same-origin fetch to the BOLTcrm API with the session cookie. */
export async function apiFetch<T>(
  path: string,
  init: { method?: string; body?: unknown; headers?: Record<string, string> } = {},
): Promise<T> {
  const headers = { ...(init.body !== undefined ? { 'content-type': 'application/json' } : {}), ...init.headers };
  const res = await fetch(path, {
    method: init.method ?? 'GET',
    credentials: 'include',
    // e.g. `Idempotency-Key` on creates that must not repeat (api.md › Writes).
    headers: Object.keys(headers).length ? headers : undefined,
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  const text = await res.text();
  const json = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const e = json?.error ?? {};
    throw new ApiError(res.status, e.code ?? 'error', e.message ?? json?.message ?? res.statusText, e.details);
  }
  return json as T;
}
